import {
  doc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  orderBy,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore'
import { constructQuery } from '@core/utils/utils'
import { uniqBy } from 'lodash'
import store from '@/store'
import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'
import i18n from '@/libs/i18n'
import { getUnix } from '@/libs/date-format'

export default {
  namespaced: true,
  state: {
    list: [],
    filteredList: [],
    current: {},
    currentData: {},
  },
  getters: {
    getAll: state => state.list.map(docRef => ({ ...docRef.data(), id: docRef.id, pageSlug: docRef.data().pageMetadata.slug })).filter(el => !el.deletedAt),
    getById: state => id => state.list.find(el => el.id === id),
    getCurrent: state => state.current,
    getCurrentData: state => state.currentData,
    getFilteredList: state => state.filteredList.map(docRef => ({ ...docRef.data(), id: docRef.id })),
  },
  mutations: {
    UPDATE_LIST(state, payload) {
      const { docs, pagination } = payload

      state.list = (!pagination) ? docs : uniqBy([...state.list, ...docs], 'id')
    },
    UPDATE_FILTERED_LIST(state, payload) {
      state.filteredList = payload
    },
    SET_CURRENT(state, payload) {
      state.current = payload
      state.currentData = ({ ...payload.data(), id: payload.id })
    },
  },
  actions: {
    /**
     * Fetches all
     *
     * @param {Object}
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    fetchAll({ commit }, payload) {
      return new Promise((resolve, reject) => {
        const { transactions } = tenantCtx()

        const { queryOptions, useProdEnvironment, pagination = false } = payload

        // For this version let us only focus on 5 completed transactions
        const q = constructQuery(transactions, 'createdAt', {
          max: 5,
          ...queryOptions,
          order: 'desc',
          where: [
            where('status', '==', 'COMPLETE'),
            where('useProdEnvironment', '==', useProdEnvironment),
          ],
        })

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_LIST', { docs: querySnapshot.docs, pagination })
            resolve(querySnapshot.docs)
          })
          .catch(error => {
            reject(error)
            console.debug(error)
          })
      })
    },

    /**
     * Fetches on search query
     *
     * @param {Object}
     * @param {Object} queryOptions
     *
     * @returns {Promise}
     */
    fetchFiltered({ commit }, queryOptions) {
      return new Promise((resolve, reject) => {
        const { transactions } = tenantCtx()

        const q = constructQuery(transactions, 'createdAt', {
          ...queryOptions,
        })

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_FILTERED_LIST', querySnapshot.docs)
            resolve(querySnapshot.docs)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the transactions'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * resets the filter list
     *
     * @param {Object}
     *
     * @returns {Promise}
     */
    resetFiltered({ commit }) {
      return new Promise(resolve => {
        commit('UPDATE_FILTERED_LIST', [])
        resolve()
      })
    },

    /**
     * Fetches all the transactions but with extra filter logic.
     *
     * @param {Object} commit
     * @param {Object} filter Variables for filtering the dataset
     * @returns
     */
    fetchEntire({ commit }, {
      max, from, to, order, offset,
    }) {
      return new Promise((resolve, reject) => {
        const { transactions } = tenantCtx()

        let q = constructQuery(transactions, 'createdAt', {
          max, order, offset, where: [where('status', 'in', ['COMPLETE', 'ERROR'])],
        })

        if (from) {
          q = constructQuery(transactions, 'createdAt', {
            max,
            order,
            offset,
            where: [
              where('status', 'in', ['COMPLETE', 'ERROR']),
              where('createdAt', '>=', getUnix(from)),
              where('createdAt', '<=', (to ? getUnix(to) : getUnix())),
            ],
          })
        }

        getDocs(q)
          .then(querySnapshot => {
            commit('UPDATE_LIST', { docs: querySnapshot.docs, pagination: true })
            resolve(querySnapshot.docs)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Fetches a document by the given ID
     *
     * @param {string} id The logs document ID
     *
     * @returns {Promise} Retrieves all fields in the document as an Object or undefined if the document doesn't exist.
     */
    fetchById({}, id) {
      return new Promise((resolve, reject) => {
        const { transactions } = tenantCtx()
        const docRef = doc(transactions, id)

        getDoc(docRef)
          .then(docSnapshot => {
            if (!docSnapshot.exists()) {
              reject(new Error('404'))
            }

            resolve(docSnapshot)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong retrieving the logs'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches a document by the current item ID
     *
     * @param {string} id
     */
    fetchCurrent({ dispatch, commit }, id) {
      return new Promise((resolve, reject) => {
        dispatch('fetchById', id)
          .then(res => {
            commit('SET_CURRENT', res)
            resolve(res)
          })
          .catch(error => reject(error))
      })
    },

    /**
     * Soft deletes a document
     *
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    softDelete({}, payload) {
      return new Promise((resolve, reject) => {
        const { transactions } = tenantCtx()
        const docRef = doc(transactions, payload._ID)

        updateDoc(docRef, {
          deletedAt: getUnix(),
        })
          .then(() => {
            resolve(true)
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    /**
     * Listens to incomming transactions
     *
     * @param {Object}
     *
     * @returns {Object}
     */
    async transactionListener({ dispatch }, payload) {
      // Get the initial results before listening
      await dispatch('fetchAll', { useProdEnvironment: payload.useProdEnvironment })

      const { transactions } = tenantCtx()
      const q = query(
        transactions,
        where('status', '==', 'COMPLETE'),
        where('useProdEnvironment', '==', payload.useProdEnvironment),
        limit(5),
        orderBy('createdAt', 'desc'),
      )

      // Now listen to changes and refetch
      const unsubscribe = onSnapshot(q, () => {
        dispatch('fetchAll', { useProdEnvironment: payload.useProdEnvironment })
      })

      return unsubscribe
    },
  },
}
